/*
 * ตาราง: แก้ตารางแบบ Excel (E1, choice 28 ต.ค. 2569 13:41). A full-screen sheet over the work: the table as a grid of
 * cells — tap a cell, type in the box above (Thai typing on iPad works as in any text box), choose a range, insert /
 * delete rows and columns, merge, bold / align / colours / size, column widths, วางจาก Excel. Every change goes
 * through src/table/tableModel.ts (the same checker as opening and the server — BUG-13) and is undone with the
 * sheet's own ↶ ↷. "เสร็จ" draws the table on the page again (one undo step on the page); "ยกเลิก" leaves it as it was.
 *
 * Also here: องค์ประกอบ → ตาราง (a new 3 × 4 table), a double tap on a table, the toolbar's "แก้ตาราง", a table pulled
 * bigger with its handles (columns change, not the letters), and laying tables out again once their font has come.
 */
import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { FONT_CATALOG } from '../fonts/catalog';
import {
  cellName, checkTable, clearText, deleteCols, deleteRows, evenCols, growSel, insertCols, insertRows, isCovered,
  mergeAt, mergeCells, newTable, pasteGrid, setColWidth, setLook, setTableLook, setText, splitCells,
  TABLE_COL_MIN, TABLE_SIDE_MAX, type CellLook, type Edited, type PasteGrid, type TableData, type TableSel
} from '../table/tableModel';
import { looksLikeCells, parseClipHtml, parseTsv } from '../table/tablePaste';
import { bakeScale, isTable, loadTableFonts, makeTable, rebuildTable, type TableGroup } from '../table/tableFabric';
import { $, $in } from './dom';
import { closeSheets } from './sheets';
import { toast } from './toast';

export interface TableHooks {
  /** why the page cannot be changed now (someone else's work · a page loading — BUG-10), or null */
  cannotEdit: () => string | null;
}

const DOUBLE_MS = 450, DOUBLE_PX = 14;
const UNDO_MAX = 60;
const ALIGN_CSS = { l: 'left', c: 'center', r: 'right' } as const;
const VALIGN_CSS = { t: 'top', m: 'middle', b: 'bottom' } as const;

export function initTableView(ed: Editor, history: History, h: TableHooks) {
  const { canvas } = ed;
  const view = $('tbl'), grid = $<HTMLTableElement>('tbGrid'), text = $<HTMLTextAreaElement>('tbText'), msg = $('tbMsg');
  const wrap = $('tbWrap');

  /** the table being edited (a copy; the page changes only on "เสร็จ") and the group it came from */
  let draft: TableData | null = null, target: TableGroup | null = null, original = '';
  let anchor = { r: 0, c: 0 }, sel: TableSel = { r0: 0, c0: 0, r1: 0, c1: 0 }, range = false;
  /** typing into one cell is one undo step (until another cell is chosen) */
  let typing = false, busy = false;
  let undo: string[] = [], redo: string[] = [];

  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); msg.setAttribute('role', ok ? 'status' : 'alert'); };

  /* ---------- open / close ---------- */

  function open(g: TableGroup) {
    const why = h.cannotEdit();
    if (why) { toast(why, true); return; }
    const k = checkTable(g.table);
    if (!k.ok) { toast(`ตารางนี้ข้อมูลเสีย แก้ไม่ได้: ${k.error}`, true); return; }
    target = g; draft = k.value; original = JSON.stringify(k.value);
    undo = []; redo = []; typing = false; range = false; busy = false;
    anchor = { r: 0, c: 0 }; sel = growSel(draft, { r0: 0, c0: 0, r1: 0, c1: 0 });
    fillFonts();
    say('');
    view.hidden = false;
    render();
    text.value = draft.cells[0][0].t;
  }

  function close() {
    view.hidden = true;
    draft = null; target = null; busy = false;
    text.blur();
  }

  $('tbCancel').onclick = () => {
    if (draft && JSON.stringify(draft) !== original && !confirm('ทิ้งสิ่งที่แก้ในตารางนี้? ตารางบนหน้างานจะเป็นเหมือนเดิม')) return;
    close();
  };

  $('tbDone').onclick = () => void finish();
  async function finish() {
    if (!draft || busy) return;
    if (JSON.stringify(draft) === original) { close(); return; }
    // the same checker as opening and the server (BUG-13): never put on the page what could not be saved
    const k = checkTable(draft);
    if (!k.ok) { say(`ยังใส่ตารางนี้ไม่ได้: ${k.error}`); return; }
    const why = h.cannotEdit();
    if (why) { say(why); return; }
    busy = true; paintBusy();
    const failed = await loadTableFonts(k.value);
    busy = false; paintBusy();
    if (failed) { say(failed); return; }
    // the page may have changed while the font came (BUG-31): the table must still be there, the page not loading
    const g = target;
    const again = h.cannotEdit();
    if (again) { say(again); return; }
    if (!g || !canvas.getObjects().includes(g)) {
      say('ตารางนี้ไม่อยู่บนหน้าที่เปิดอยู่แล้ว (เปลี่ยนหน้า เลิกทำ หรือถูกลบ) สิ่งที่แก้ยังไม่ได้ใส่ — กดยกเลิก แล้วแตะตารางสองครั้งเพื่อแก้ใหม่');
      return;
    }
    rebuildTable(g, k.value);
    canvas.requestRenderAll();
    // one step on the page's undo (main.ts records object:modified), the toolbar and layers see it
    canvas.fire('object:modified', { target: g });
    close();
  }

  function paintBusy() {
    $<HTMLButtonElement>('tbDone').disabled = busy;
    $('tbDone').textContent = busy ? 'กำลังโหลดฟอนต์…' : 'เสร็จ';
  }

  /* ---------- undo inside the sheet ---------- */

  function paintHist() {
    $<HTMLButtonElement>('tbUndo').disabled = !undo.length;
    $<HTMLButtonElement>('tbRedo').disabled = !redo.length;
  }
  $('tbUndo').onclick = () => {
    if (!draft || !undo.length) return;
    redo.push(JSON.stringify(draft));
    draft = JSON.parse(undo.pop()!) as TableData;
    typing = false; say(''); keepSel(); render(); text.value = cellText();
  };
  $('tbRedo').onclick = () => {
    if (!draft || !redo.length) return;
    undo.push(JSON.stringify(draft));
    draft = JSON.parse(redo.pop()!) as TableData;
    typing = false; say(''); keepSel(); render(); text.value = cellText();
  };

  /** an edit: applied with its notes, or its reason shown and nothing changed */
  function apply(res: Edited, after?: (t: TableData) => void): boolean {
    if (!draft) return false;
    if (!res.ok) { say(res.error); return false; }
    undo.push(JSON.stringify(draft));
    if (undo.length > UNDO_MAX) undo.shift();
    redo = [];
    draft = res.value;
    typing = false;
    after?.(draft);
    keepSel();
    say(res.notes.join(' · '), true);
    render();
    text.value = cellText();
    return true;
  }

  /* ---------- choosing cells ---------- */

  /** the choice still inside the table after rows/columns went (and grown over merges) */
  function keepSel() {
    if (!draft) return;
    const R = draft.rows.length - 1, C = draft.cols.length - 1;
    anchor = { r: Math.min(anchor.r, R), c: Math.min(anchor.c, C) };
    const m = mergeAt(draft, anchor.r, anchor.c);
    if (m) anchor = { r: m.r, c: m.c };
    sel = growSel(draft, { r0: Math.min(sel.r0, R), c0: Math.min(sel.c0, C), r1: Math.min(sel.r1, R), c1: Math.min(sel.c1, C) });
  }
  const cellText = () => draft ? draft.cells[anchor.r][anchor.c].t : '';

  function choose(r: number, c: number, extend: boolean) {
    if (!draft) return;
    if (extend) sel = growSel(draft, { r0: anchor.r, c0: anchor.c, r1: r, c1: c });
    else {
      const m = mergeAt(draft, r, c);
      anchor = m ? { r: m.r, c: m.c } : { r, c };
      sel = growSel(draft, { r0: anchor.r, c0: anchor.c, r1: anchor.r, c1: anchor.c });
    }
    typing = false;
    text.value = cellText();
    say('');
    paintSel();
  }
  function chooseCols(c: number, extend: boolean) {
    if (!draft) return;
    const c0 = extend ? Math.min(anchor.c, c) : c, c1 = extend ? Math.max(anchor.c, c) : c;
    if (!extend) anchor = { r: 0, c };
    sel = growSel(draft, { r0: 0, c0, r1: draft.rows.length - 1, c1 });
    typing = false; text.value = cellText(); say(''); paintSel();
  }
  function chooseRows(r: number, extend: boolean) {
    if (!draft) return;
    const r0 = extend ? Math.min(anchor.r, r) : r, r1 = extend ? Math.max(anchor.r, r) : r;
    if (!extend) anchor = { r, c: 0 };
    sel = growSel(draft, { r0, c0: 0, r1, c1: draft.cols.length - 1 });
    typing = false; text.value = cellText(); say(''); paintSel();
  }

  $('tbRange').onclick = () => { range = !range; $('tbRange').setAttribute('aria-pressed', String(range)); };

  /* ---------- the grid ---------- */

  /** screen px per page px for the grid (columns readable on a phone, the table's proportions kept) */
  function scale(t: TableData): number {
    const total = t.cols.reduce((a, b) => a + b, 0);
    const room = Math.max(280, (wrap.clientWidth || 600) - 70);
    return Math.min(1, Math.max(0.2, room / total));
  }
  const shownWidth = (t: TableData, c: number, k = scale(t)) => Math.max(64, Math.round(t.cols[c] * k));

  function render() {
    if (!draft) return;
    const t = draft, k = scale(t);
    grid.textContent = '';
    grid.style.fontFamily = `"${t.font}", "Sarabun", sans-serif`;
    const cg = document.createElement('colgroup');
    const rh = document.createElement('col'); rh.style.width = '40px'; cg.append(rh);
    t.cols.forEach((_, c) => { const col = document.createElement('col'); col.style.width = `${shownWidth(t, c, k)}px`; cg.append(col); });
    grid.append(cg);
    grid.style.width = `${40 + t.cols.reduce((a, _, c) => a + shownWidth(t, c, k), 0)}px`;
    const head = document.createElement('tr');
    const corner = document.createElement('th'); corner.className = 'tbch'; head.append(corner);
    t.cols.forEach((_, c) => {
      const th = document.createElement('th');
      th.className = 'tbch'; th.dataset.col = String(c);
      th.textContent = cellName(0, c).replace(/\d+$/, '');
      const grip = document.createElement('span'); grip.className = 'tbrs'; grip.dataset.grip = String(c);
      grip.setAttribute('aria-label', `ลากเพื่อปรับความกว้างคอลัมน์ ${th.textContent}`);
      th.append(grip);
      head.append(th);
    });
    grid.append(head);
    for (let r = 0; r < t.rows.length; r++) {
      const tr = document.createElement('tr');
      const th = document.createElement('th'); th.className = 'tbrh'; th.dataset.row = String(r); th.textContent = String(r + 1);
      tr.append(th);
      for (let c = 0; c < t.cols.length; c++) {
        if (isCovered(t, r, c)) continue;
        const m = mergeAt(t, r, c), cell = t.cells[r][c];
        const td = document.createElement('td');
        td.dataset.r = String(r); td.dataset.c = String(c);
        if (m) { td.rowSpan = m.rs; td.colSpan = m.cs; }
        td.textContent = cell.t;
        td.style.fontWeight = cell.b ? '700' : '400';
        td.style.textAlign = ALIGN_CSS[cell.al ?? 'l'];
        td.style.verticalAlign = VALIGN_CSS[cell.va ?? 't'];
        td.style.color = cell.c ?? t.color;
        td.style.background = cell.bg ?? '';
        // the cell's size against the table's (the grid is for editing, not to scale)
        td.style.fontSize = `${Math.max(11, Math.min(22, 14 * (cell.fs ?? t.size) / t.size))}px`;
        tr.append(td);
      }
      grid.append(tr);
    }
    paintSel();
    paintHist();
    paintTable();
  }

  function paintSel() {
    if (!draft) return;
    grid.querySelectorAll('.tbon, .tbanchor').forEach(el => el.classList.remove('tbon', 'tbanchor'));
    grid.querySelectorAll<HTMLElement>('td').forEach(td => {
      const r = Number(td.dataset.r), c = Number(td.dataset.c);
      if (r >= sel.r0 && r <= sel.r1 && c >= sel.c0 && c <= sel.c1) td.classList.add('tbon');
      if (r === anchor.r && c === anchor.c) td.classList.add('tbanchor');
    });
    grid.querySelectorAll<HTMLElement>('th[data-col]').forEach(th => { const c = Number(th.dataset.col); th.classList.toggle('tbon', c >= sel.c0 && c <= sel.c1); });
    grid.querySelectorAll<HTMLElement>('th[data-row]').forEach(th => { const r = Number(th.dataset.row); th.classList.toggle('tbon', r >= sel.r0 && r <= sel.r1); });
    const one = sel.r0 === sel.r1 && sel.c0 === sel.c1 || !!(mergeAt(draft, sel.r0, sel.c0) && (() => { const m = mergeAt(draft!, sel.r0, sel.c0)!; return m.r + m.rs - 1 === sel.r1 && m.c + m.cs - 1 === sel.c1; })());
    $('tbAddr').textContent = one ? cellName(anchor.r, anchor.c) : `${cellName(sel.r0, sel.c0)}:${cellName(sel.r1, sel.c1)}`;
    // the tools show the first chosen cell's look (inputs being typed in are not written over — BUG-20)
    const cell = draft.cells[anchor.r][anchor.c];
    $('tbBold').setAttribute('aria-pressed', String(!!cell.b));
    $('tbAlign').querySelectorAll<HTMLElement>('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.al === (cell.al ?? 'l'))));
    $('tbVAlign').querySelectorAll<HTMLElement>('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.va === (cell.va ?? 't'))));
    setIfIdle($in('tbBg'), cell.bg ?? '#ffffff');
    setIfIdle($in('tbInk'), cell.c ?? draft.color);
    setIfIdle($in('tbSize'), String(Math.round((cell.fs ?? draft.size) * 10) / 10));
    setIfIdle($in('tbWidth'), String(Math.round(draft.cols[anchor.c] * 10) / 10));
  }
  const setIfIdle = (el: HTMLInputElement | HTMLSelectElement, v: string) => { if (document.activeElement !== el) el.value = v; };

  /* taps on the grid: a cell, a column's letter, a row's number, or a column's edge (its width) */
  let drag: { pointer: number } | null = null;
  grid.addEventListener('pointerdown', e => {
    if (!draft) return;
    const el = e.target as HTMLElement;
    const grip = el.closest<HTMLElement>('[data-grip]');
    if (grip) { startResize(e, Number(grip.dataset.grip)); return; }
    const extend = range || e.shiftKey;
    const col = el.closest<HTMLElement>('th[data-col]');
    if (col) { chooseCols(Number(col.dataset.col), extend); return; }
    const row = el.closest<HTMLElement>('th[data-row]');
    if (row) { chooseRows(Number(row.dataset.row), extend); return; }
    const td = el.closest<HTMLElement>('td[data-r]');
    if (!td) return;
    choose(Number(td.dataset.r), Number(td.dataset.c), extend);
    // a mouse or pen drags a range; a finger scrolls the grid
    if (e.pointerType !== 'touch') drag = { pointer: e.pointerId };
  });
  grid.addEventListener('pointermove', e => {
    if (!drag || drag.pointer !== e.pointerId || !draft) return;
    const td = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest<HTMLElement>('#tbGrid td[data-r]');
    if (!td) return;
    const r = Number(td.dataset.r), c = Number(td.dataset.c);
    const next = growSel(draft, { r0: anchor.r, c0: anchor.c, r1: r, c1: c });
    if (next.r0 !== sel.r0 || next.c0 !== sel.c0 || next.r1 !== sel.r1 || next.c1 !== sel.c1) { sel = next; paintSel(); }
  });
  const endDrag = () => { drag = null; };
  grid.addEventListener('pointerup', endDrag);
  grid.addEventListener('pointercancel', endDrag);

  function startResize(e: PointerEvent, c: number) {
    if (!draft) return;
    e.preventDefault();
    const t = draft, k = scale(t), x0 = e.clientX, w0 = t.cols[c];
    const col = grid.querySelectorAll('col')[c + 1] as HTMLElement | undefined;
    const grip = e.target as HTMLElement;
    grip.setPointerCapture(e.pointerId);
    let w = w0;
    const move = (ev: PointerEvent) => {
      w = Math.min(TABLE_SIDE_MAX, Math.max(TABLE_COL_MIN, w0 + (ev.clientX - x0) / k));
      if (col) col.style.width = `${Math.max(64, Math.round(w * k))}px`;
    };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      if (Math.abs(w - w0) >= 0.5) apply(setColWidth(t, c, c, w)); else render();
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  /* ---------- typing ---------- */

  text.addEventListener('input', () => {
    if (!draft) return;
    const res = setText(draft, anchor.r, anchor.c, text.value);
    if (!res.ok) { say(res.error); return; }
    if (!typing) { undo.push(JSON.stringify(draft)); if (undo.length > UNDO_MAX) undo.shift(); redo = []; typing = true; }
    draft = res.value;
    const td = grid.querySelector<HTMLElement>(`td[data-r="${anchor.r}"][data-c="${anchor.c}"]`);
    if (td) td.textContent = text.value;
    if (msg.classList.contains('ok') || !msg.hidden) say('');
    paintHist();
  });

  /* ---------- วางจาก Excel ---------- */

  /** what was copied, as cells: Excel's / Google Sheets' HTML table first (merges, looks), else their tab-separated text */
  function fromClipboard(html: string, plain: string, sure: boolean): PasteGrid | null {
    if (html && /<table[\s>]/i.test(html)) {
      const g = parseClipHtml(new DOMParser().parseFromString(html, 'text/html'));
      if (g) return g;
    }
    if (plain && (sure || looksLikeCells(plain))) {
      const rows = parseTsv(plain);
      if (rows.length && rows[0].length) return { cells: rows.map(row => row.map(t => ({ t }))), merges: [], looks: false };
    }
    return null;
  }
  function paste(g: PasteGrid) {
    if (!draft) return;
    const at = { r: sel.r0, c: sel.c0 };
    const rows = g.cells.length, cols = g.cells[0].length;
    if (apply(pasteGrid(draft, at, g))) {
      anchor = at;
      sel = growSel(draft!, { r0: at.r, c0: at.c, r1: at.r + rows - 1, c1: at.c + cols - 1 });
      paintSel();
      text.value = cellText();
      say(`วาง ${rows} แถว × ${cols} คอลัมน์ ที่ ${cellName(at.r, at.c)} แล้ว${msg.textContent ? ` · ${msg.textContent}` : ''}`, true);
    }
  }
  text.addEventListener('paste', e => {
    if (!draft) return;
    const html = e.clipboardData?.getData('text/html') ?? '', plain = e.clipboardData?.getData('text/plain') ?? '';
    const g = fromClipboard(html, plain, false);
    if (!g) return;   // a sentence: pasted into this cell as typing
    e.preventDefault();
    paste(g);
  });
  $('tbPaste').onclick = () => void pasteButton();
  async function pasteButton() {
    if (!draft) return;
    let html = '', plain = '';
    try {
      const clip = navigator.clipboard as Clipboard & { read?: () => Promise<ClipboardItem[]> };
      if (clip?.read) {
        for (const item of await clip.read()) {
          if (!html && item.types.includes('text/html')) html = await (await item.getType('text/html')).text();
          if (!plain && item.types.includes('text/plain')) plain = await (await item.getType('text/plain')).text();
        }
      } else if (clip?.readText) plain = await clip.readText();
      else throw new Error('no clipboard');
    } catch {
      say('เบราว์เซอร์ไม่ให้ปุ่มนี้อ่านคลิปบอร์ด — แตะค้างในช่องพิมพ์ด้านบนแล้วเลือก "วาง" แทน (ข้อมูลจาก Excel จะลงเป็นตารางเหมือนกัน)');
      return;
    }
    const g = fromClipboard(html, plain, true);
    if (!g) { say('ในคลิปบอร์ดไม่มีข้อมูลตาราง คัดลอกช่องจาก Excel หรือ Google Sheets ก่อน แล้วกดใหม่'); return; }
    paste(g);
  }

  /* ---------- rows, columns, merges, looks ---------- */

  $('tbRowUp').onclick = () => draft && apply(insertRows(draft, sel.r0), () => { anchor = { r: sel.r0, c: anchor.c }; sel = { r0: sel.r0, c0: sel.c0, r1: sel.r0, c1: sel.c1 }; });
  $('tbRowDown').onclick = () => draft && apply(insertRows(draft, sel.r1 + 1), () => { const r = sel.r1 + 1; anchor = { r, c: anchor.c }; sel = { r0: r, c0: sel.c0, r1: r, c1: sel.c1 }; });
  $('tbColLeft').onclick = () => draft && apply(insertCols(draft, sel.c0), () => { anchor = { r: anchor.r, c: sel.c0 }; sel = { r0: sel.r0, c0: sel.c0, r1: sel.r1, c1: sel.c0 }; });
  $('tbColRight').onclick = () => draft && apply(insertCols(draft, sel.c1 + 1), () => { const c = sel.c1 + 1; anchor = { r: anchor.r, c }; sel = { r0: sel.r0, c0: c, r1: sel.r1, c1: c }; });
  $('tbDelRow').onclick = () => draft && apply(deleteRows(draft, sel.r0, sel.r1), () => { anchor = { r: sel.r0, c: anchor.c }; sel = { ...sel, r1: sel.r0 }; });
  $('tbDelCol').onclick = () => draft && apply(deleteCols(draft, sel.c0, sel.c1), () => { anchor = { r: anchor.r, c: sel.c0 }; sel = { ...sel, c1: sel.c0 }; });
  $('tbMerge').onclick = () => draft && apply(mergeCells(draft, sel), () => { anchor = { r: sel.r0, c: sel.c0 }; });
  $('tbSplit').onclick = () => draft && apply(splitCells(draft, sel));
  $('tbClear').onclick = () => draft && apply(clearText(draft, sel));
  const look = (l: CellLook) => draft && apply(setLook(draft, sel, l));
  $('tbBold').onclick = () => { if (draft) look({ b: draft.cells[anchor.r][anchor.c].b ? null : true }); };
  $('tbAlign').onclick = e => { const b = (e.target as Element).closest<HTMLElement>('button[data-al]'); if (b) look({ al: b.dataset.al as CellLook['al'] }); };
  $('tbVAlign').onclick = e => { const b = (e.target as Element).closest<HTMLElement>('button[data-va]'); if (b) look({ va: b.dataset.va as CellLook['va'] }); };
  // colours and numbers apply when the choice is made (change), not on every step of the picker
  $in('tbBg').onchange = () => look({ bg: $in('tbBg').value.toLowerCase() });
  $('tbNoBg').onclick = () => look({ bg: null });
  $in('tbInk').onchange = () => look({ c: $in('tbInk').value.toLowerCase() });
  $in('tbSize').onchange = () => {
    const v = $in('tbSize').value.trim();
    if (!v) { look({ fs: null }); return; }
    if (!Number.isFinite(Number(v))) { say('ขนาดตัวอักษรต้องเป็นตัวเลข'); return; }
    look({ fs: Number(v) });
  };
  $('tbEven').onclick = () => draft && apply(evenCols(draft, sel.c0, sel.c1));
  $in('tbWidth').onchange = () => {
    const v = Number($in('tbWidth').value);
    if (!draft) return;
    if (!Number.isFinite(v)) { say('ความกว้างต้องเป็นตัวเลข'); return; }
    apply(setColWidth(draft, sel.c0, sel.c1, v));
  };

  /* ---------- the whole table ---------- */

  let fontsFilled = false;
  function fillFonts() {
    if (fontsFilled) return;
    fontsFilled = true;
    const box = $<HTMLSelectElement>('tbFont');
    for (const f of FONT_CATALOG.filter(f => f.thai)) box.append(new Option(f.family, f.family));
  }
  function paintTable() {
    if (!draft) return;
    setIfIdle($<HTMLSelectElement>('tbFont'), draft.font);
    setIfIdle($in('tbBase'), String(Math.round(draft.size * 10) / 10));
    setIfIdle($in('tbColor'), draft.color);
    setIfIdle($in('tbLine'), draft.line);
    setIfIdle($in('tbLw'), String(Math.round(draft.lw * 10) / 10));
    setIfIdle($in('tbPad'), String(Math.round(draft.pad * 10) / 10));
  }
  const tableLook = (l: Parameters<typeof setTableLook>[1]) => draft && apply(setTableLook(draft, l));
  $<HTMLSelectElement>('tbFont').onchange = () => tableLook({ font: $<HTMLSelectElement>('tbFont').value });
  const numberOf = (id: string, name: string, put: (n: number) => void) => () => {
    const v = Number($in(id).value);
    if ($in(id).value.trim() === '' || !Number.isFinite(v)) { say(`${name}ต้องเป็นตัวเลข`); paintTable(); return; }
    put(v);
  };
  $in('tbBase').onchange = numberOf('tbBase', 'ขนาดตัวอักษร', n => tableLook({ size: n }));
  $in('tbLw').onchange = numberOf('tbLw', 'ความหนาเส้น', n => tableLook({ lw: n }));
  $in('tbPad').onchange = numberOf('tbPad', 'ระยะห่างจากขอบช่อง', n => tableLook({ pad: n }));
  $in('tbColor').onchange = () => tableLook({ color: $in('tbColor').value.toLowerCase() });
  $in('tbLine').onchange = () => tableLook({ line: $in('tbLine').value.toLowerCase() });

  view.addEventListener('keydown', e => { if (e.key === 'Escape') $('tbCancel').click(); });

  /* ---------- on the page ---------- */

  /** องค์ประกอบ → ตาราง: a 3 × 4 table (a header row) in the middle of the page, then its editor */
  $('eTable').onclick = () => void insert();
  async function insert() {
    const why = h.cannotEdit();
    if (why) { toast(why, true); return; }
    const t = newTable(3, 4, ed.unit());
    const failed = await loadTableFonts(t);
    if (failed) { toast(failed.replace('ตารางยังไม่ได้เปลี่ยน', 'ยังไม่ได้ใส่ตาราง'), true); return; }
    const again = h.cannotEdit();
    if (again) { toast(again, true); return; }
    const g = makeTable(t);
    ed.place(g);
    if (!matchMedia('(min-width: 900px)').matches) closeSheets();
    open(g);
  }

  // the toolbar's "แก้ตาราง" (src/ui/toolbar.ts)
  canvas.on('table:edit' as never, ((opt: { target?: fabric.Object }) => { if (isTable(opt.target)) open(opt.target); }) as never);

  // a double tap on a table (a finger or a mouse), as on a shape (src/ui/shapePanel.ts)
  let last: { g: fabric.Object; at: number; x: number; y: number } | null = null;
  canvas.on('mouse:down', opt => {
    const e = opt.e as MouseEvent | TouchEvent;
    if ('touches' in e && e.touches.length > 1) { last = null; return; }
    const p = canvas.getPointer(e);
    const at = new fabric.Point(p.x, p.y);
    const top = [...ed.userObjects()].reverse().find(o => o.visible !== false && o.containsPoint(at, undefined, true));
    if (!isTable(top)) { last = null; return; }
    const now = Date.now();
    if (last && last.g === top && now - last.at < DOUBLE_MS && Math.hypot(p.x - last.x, p.y - last.y) * canvas.getZoom() < DOUBLE_PX) {
      last = null;
      open(top);
      return;
    }
    last = { g: top, at: now, x: p.x, y: p.y };
  });

  // pulled bigger/smaller with its handles: the columns and rows take the size, the letters keep theirs
  // (before main.ts records the change: this listener is added first)
  canvas.on('object:modified', opt => {
    const g = opt.target;
    if (isTable(g) && bakeScale(g)) canvas.requestRenderAll();
  });

  /**
   * After a page or work is opened and its fonts have come (main.ts): every table drawn again with the real font —
   * text measured with a stand-in while the font was loading would sit wrong in its cells. Not an edit (no undo step).
   */
  async function relayout() {
    const tables = ed.userObjects().filter(isTable);
    if (!tables.length) return;
    const fonts = new Map(tables.map(g => [g.table.font, g.table] as const));
    const failed = (await Promise.all([...fonts.values()].map(loadTableFonts))).filter(Boolean);
    if (failed.length) { toast(failed[0]!.replace('(ตารางยังไม่ได้เปลี่ยน)', '— ตารางแสดงด้วยฟอนต์สำรอง'), true); return; }
    if (history.loading) return;
    for (const g of tables) {
      if (!canvas.getObjects().includes(g)) continue;   // another page or work opened meanwhile (BUG-10)
      const k = checkTable(g.table);
      if (k.ok) rebuildTable(g, k.value);
    }
    canvas.requestRenderAll();
  }

  return { open, relayout, isOpen: () => !view.hidden };
}
